/**
 * Horizon overrides for Shopify.actions and Sfilato storefront guards:
 * - updateCart: emit events from the cart drawer scope.
 * - openCart: open the cart drawer (fall back to /cart when absent).
 * - collection hero: keep cached collection HTML aligned with category media.
 * - localization trigger: keep cached headers from exposing raw currency codes.
 */

const collectionHeroMedia = {
  'new-arrivals': [
    ['/collections/new-arrivals', 'sfilato-campaign-riviera-dress.jpg', 'Sfilato latest arrivals on a Mediterranean terrace', 'Just-arrived edit'],
    ['/collections/clothing', 'sfilato-campaign-terrace-hero.jpg', 'Sfilato complete look styled for Riviera days', 'Complete looks'],
  ],
  clothing: [
    ['/collections/clothing', 'sfilato-campaign-terrace-hero.jpg', 'Sfilato complete womenswear edit on a Riviera terrace', 'Five-minute styling'],
    ['/collections/outerwear', 'sfilato-campaign-tailoring-terrace.jpg', 'Sfilato tailoring and outerwear with clean structure', 'Tailored structure'],
  ],
  dresses: [
    ['/collections/dresses', 'sfilato-campaign-riviera-dress.jpg', 'Sfilato dress campaign on a Mediterranean terrace', 'Riviera dress edit'],
    ['/search?type=product&q=party%20dress', 'sfilato-campaign-evening-terrace.jpg', 'Sfilato evening dress on a Riviera terrace', 'Evening movement'],
  ],
  outerwear: [
    ['/collections/outerwear', 'sfilato-campaign-tailoring-terrace.jpg', 'Sfilato tailoring and outerwear on a Riviera terrace', 'Outerwear expertise'],
    ['/search?type=product&q=Cashmere%20Touch', 'sfilato-stock-cashmere-texture.jpg', 'Sfilato Cashmere Touch material texture', 'Cashmere Touch'],
  ],
  knitwear: [
    ['/search?type=product&q=Cashmere%20Touch', 'sfilato-stock-cashmere-texture.jpg', 'Sfilato Cashmere Touch knitwear texture', 'Cashmere Touch'],
    ['/collections/knitwear', 'sfilato-campaign-terrace-hero.jpg', 'Sfilato soft knitwear layer styled with Riviera ease', 'Soft layers'],
  ],
  tops: [
    ['/collections/tops', 'sfilato-campaign-terrace-hero.jpg', 'Sfilato tops styled into a complete look', 'Polished layers'],
    ['/collections/pants', 'sfilato-campaign-tailoring-terrace.jpg', 'Sfilato tailoring pairings for tops and trousers', 'Easy pairings'],
  ],
  pants: [
    ['/collections/clothing', 'sfilato-campaign-tailoring-terrace.jpg', 'Sfilato tailoring styled on a Riviera terrace', 'Tailoring edit'],
    ['/search?type=product&q=office', 'sfilato-campaign-terrace-hero.jpg', 'Sfilato complete look for office and travel', 'Office to evening'],
  ],
  suits: [
    ['/collections/clothing', 'sfilato-campaign-tailoring-terrace.jpg', 'Sfilato tailoring styled on a Riviera terrace', 'Tailoring edit'],
    ['/search?type=product&q=office', 'sfilato-campaign-terrace-hero.jpg', 'Sfilato complete look for office and travel', 'Office to evening'],
  ],
  sets: [
    ['/collections/clothing', 'sfilato-campaign-tailoring-terrace.jpg', 'Sfilato tailoring styled on a Riviera terrace', 'Tailoring edit'],
    ['/search?type=product&q=office', 'sfilato-campaign-terrace-hero.jpg', 'Sfilato complete look for office and travel', 'Office to evening'],
  ],
  skirts: [
    ['/collections/clothing', 'sfilato-campaign-terrace-hero.jpg', 'Sfilato complete-look shapes styled on a Riviera terrace', 'Complete-look shapes'],
    ['/search?type=product&q=vacation%20dress', 'sfilato-campaign-riviera-dress.jpg', 'Sfilato Riviera styling for day-to-evening movement', 'Day to evening'],
  ],
  shorts: [
    ['/collections/clothing', 'sfilato-campaign-terrace-hero.jpg', 'Sfilato complete-look shapes styled on a Riviera terrace', 'Complete-look shapes'],
    ['/search?type=product&q=vacation%20dress', 'sfilato-campaign-riviera-dress.jpg', 'Sfilato Riviera styling for day-to-evening movement', 'Day to evening'],
  ],
  jumpsuits: [
    ['/collections/clothing', 'sfilato-campaign-terrace-hero.jpg', 'Sfilato complete-look shapes styled on a Riviera terrace', 'Complete-look shapes'],
    ['/search?type=product&q=vacation%20dress', 'sfilato-campaign-riviera-dress.jpg', 'Sfilato Riviera styling for day-to-evening movement', 'Day to evening'],
  ],
  bags: [
    ['/collections/bags', 'sfilato-campaign-detail-bag-scarf.jpg', 'Sfilato Gigi bag with silk scarf styling detail', 'Gigi bag edit'],
    ['/collections/accessories', 'sfilato-campaign-accessory-table.jpg', 'Sfilato crystal talismans and finishing accessories', 'Crystal talismans'],
  ],
  shoes: [
    ['/collections/shoes', 'sfilato-campaign-accessory-table.jpg', 'Sfilato shoes and finishing accessories', 'Shoes for the edit'],
    ['/collections/dresses', 'sfilato-campaign-evening-terrace.jpg', 'Sfilato evening dress styling on a terrace', 'Evening balance'],
  ],
  accessories: [
    ['/collections/accessories', 'sfilato-campaign-accessory-table.jpg', 'Sfilato finishing accessories and crystal talismans', 'Talismans and finishers'],
    ['/collections/bags', 'sfilato-campaign-detail-bag-scarf.jpg', 'Sfilato bag and silk scarf styling detail', 'Gigi styling'],
  ],
  sale: [
    ['/collections/sale', 'sfilato-campaign-evening-terrace.jpg', 'Sfilato limited pieces styled for evening movement', 'Last Pieces edit'],
    ['/collections/new-arrivals', 'sfilato-campaign-riviera-dress.jpg', 'Sfilato new arrivals with Riviera polish', 'New arrivals'],
  ],
};

function assetBaseFromHero(hero) {
  const image = hero.querySelector('img[src*="/assets/"]');
  const source = image?.currentSrc || image?.src || '';

  if (!source) return '';

  const url = new URL(source, window.location.origin);
  const assetIndex = url.pathname.lastIndexOf('/assets/');

  if (assetIndex < 0) return '';

  return `${url.origin}${url.pathname.slice(0, assetIndex + '/assets/'.length)}`;
}

function normalizeCollectionHeroMedia() {
  const hero = document.querySelector('.sfilato-collection-hero[data-hero-context]');
  if (!hero) return;

  const context = hero.getAttribute('data-hero-context');
  const cards = collectionHeroMedia[context];
  if (!cards) return;

  const assetBase = assetBaseFromHero(hero);
  const links = hero.querySelectorAll('.sfilato-collection-hero__media .sfilato-collection-hero__image-link');

  cards.forEach(([href, imageName, alt, label], index) => {
    const link = links[index];
    if (!(link instanceof HTMLAnchorElement)) return;

    link.href = href;

    const image = link.querySelector('img');
    if (image instanceof HTMLImageElement && assetBase) {
      image.removeAttribute('srcset');
      image.src = `${assetBase}${imageName}`;
      image.alt = alt;
    }

    const labelElement = link.querySelector('span');
    if (labelElement) {
      labelElement.textContent = label;
    }
  });
}

function scheduleCollectionHeroNormalization() {
  normalizeCollectionHeroMedia();
  requestAnimationFrame(normalizeCollectionHeroMedia);
  setTimeout(normalizeCollectionHeroMedia, 800);
}

function normalizeLocalizationTriggerLabels() {
  const labels = document.querySelectorAll(
    '.dropdown-localization__button .currency-code, .drawer-localization__button .currency-code'
  );

  for (const label of labels) {
    const currentText = label.textContent?.trim() || '';

    if (!label.getAttribute('data-currency-code') && /^[A-Z]{3}$/.test(currentText)) {
      label.setAttribute('data-currency-code', currentText);
    }

    label.textContent = 'Region';
  }
}

let localizationTriggerObserverStarted = false;

function observeLocalizationTriggerLabels() {
  if (localizationTriggerObserverStarted || !document.documentElement) return;

  localizationTriggerObserverStarted = true;

  const observer = new MutationObserver(() => {
    normalizeLocalizationTriggerLabels();
  });

  observer.observe(document.documentElement, {
    childList: true,
    characterData: true,
    subtree: true,
  });

  setTimeout(() => {
    observer.disconnect();
  }, 15000);
}

function scheduleLocalizationTriggerNormalization() {
  normalizeLocalizationTriggerLabels();
  requestAnimationFrame(normalizeLocalizationTriggerLabels);
  setTimeout(normalizeLocalizationTriggerLabels, 800);
  setTimeout(normalizeLocalizationTriggerLabels, 2000);
  setTimeout(normalizeLocalizationTriggerLabels, 5000);
  observeLocalizationTriggerLabels();
}

function init() {
  const actions = window.Shopify?.actions;
  if (!actions) return;

  actions.updateCart.configure({
    eventTarget: () => document.querySelector('theme-drawer#cart-drawer') ?? document,
  });

  actions.openCart.configure({
    async handler() {
      /** @type {HTMLElement & {open?: () => void} | null} */
      const drawer = document.querySelector('theme-drawer#cart-drawer');

      if (drawer?.open) {
        drawer.open();
      } else {
        window.location.href = Theme.routes.cart_url || '/cart';
      }
    },
  });
}

// Run immediately if the standard-actions bundle has already attached
// `Shopify.actions`; otherwise wait for DOMContentLoaded, which fires after
// all module scripts have executed regardless of document order.
if (window.Shopify?.actions) {
  init();
} else {
  document.addEventListener('DOMContentLoaded', init, { once: true });
}

if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', scheduleCollectionHeroNormalization, { once: true });
  document.addEventListener('DOMContentLoaded', scheduleLocalizationTriggerNormalization, { once: true });
} else {
  scheduleCollectionHeroNormalization();
  scheduleLocalizationTriggerNormalization();
}
